import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Loader2, Languages, Upload, X, Image as ImageIcon, Video as VideoIcon } from 'lucide-react';
import axios from 'axios';

import { API, API_URL } from '@/config';

// Capture the first-second frame of a local video File as a JPEG blob so we
// can use it as the video's thumbnail automatically.
const captureVideoThumbnail = (file) => new Promise((resolve) => {
  try {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.muted = true;
    video.playsInline = true;
    video.crossOrigin = 'anonymous';
    let settled = false;
    const done = (blob) => {
      if (settled) return;
      settled = true;
      try { URL.revokeObjectURL(url); } catch (e) { /* ignore */ }
      resolve(blob);
    };
    video.onloadedmetadata = () => {
      video.currentTime = Math.min(1, (video.duration || 2) / 2);
    };
    video.onseeked = () => {
      try {
        const canvas = document.createElement('canvas');
        const w = video.videoWidth || 640;
        const h = video.videoHeight || 360;
        canvas.width = w;
        canvas.height = h;
        canvas.getContext('2d').drawImage(video, 0, 0, w, h);
        canvas.toBlob((blob) => done(blob), 'image/jpeg', 0.82);
      } catch (e) {
        done(null);
      }
    };
    video.onerror = () => done(null);
    setTimeout(() => done(null), 8000);
    video.src = url;
  } catch (e) {
    resolve(null);
  }
});

const VideoModal = ({ open, onClose, video, token, onSuccess, i18n, t }) => {
  const [loading, setLoading] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [uploadingThumbnail, setUploadingThumbnail] = useState(false);
  const [previewVideo, setPreviewVideo] = useState('');
  const [previewThumbnail, setPreviewThumbnail] = useState('');
  const videoInputRef = useRef(null);
  const thumbnailInputRef = useRef(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    video_url: '',
    thumbnail_url: '',
    duration: 0,
    category: 'basics',
    source_language: 'sv',
    order: 0
  });

  useEffect(() => {
    if (video) {
      const videoUrl = video.video_url || '';
      const thumbnailUrl = video.thumbnail_url || '';
      setFormData({
        title: video.title?.sv || video.title?.[i18n.language] || video.title?.en || '',
        description: video.description?.sv || video.description?.[i18n.language] || video.description?.en || '',
        video_url: videoUrl,
        thumbnail_url: thumbnailUrl,
        duration: video.duration || 0,
        category: video.category || 'basics',
        source_language: 'sv',
        order: video.order || 0
      });
      setPreviewVideo(videoUrl);
      // For preview, add backend URL if it's a relative path
      const fullThumbnailUrl = thumbnailUrl.startsWith('http') ? thumbnailUrl : `${API_URL}${thumbnailUrl}`;
      setPreviewThumbnail(fullThumbnailUrl);
    } else {
      setFormData({
        title: '',
        description: '',
        video_url: '',
        thumbnail_url: '',
        duration: 0,
        category: 'basics',
        source_language: i18n.language,
        order: 0
      });
      setPreviewVideo('');
      setPreviewThumbnail('');
    }
  }, [video, open, i18n.language]);

  const handleVideoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['video/mp4', 'video/webm', 'video/avi', 'video/quicktime'];
    if (!validTypes.includes(file.type)) {
      toast.error(i18n.language === 'ar' 
        ? 'صيغة الفيديو غير مدعومة. استخدم: MP4, WEBM, AVI, MOV'
        : 'Invalid video type. Use: MP4, WEBM, AVI, MOV'
      );
      return;
    }

    // Validate file size (100MB)
    if (file.size > 100 * 1024 * 1024) {
      toast.error(i18n.language === 'ar' 
        ? 'حجم الفيديو يجب أن يكون أقل من 100MB'
        : 'Video size must be less than 100MB'
      );
      return;
    }

    setUploadingVideo(true);
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);

      const response = await axios.post(
        `${API_URL}/api/upload/video`,
        uploadFormData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response.data.success) {
        const videoUrl = response.data.url;

        setFormData({
          ...formData,
          video_url: videoUrl,
          thumbnail_url: ''
        });
        setPreviewVideo(videoUrl);
        toast.success(i18n.language === 'ar' ? 'تم رفع الفيديو! ✅' : 'Video uploaded! ✅');

        // Auto-generate thumbnail from the video's first frame.
        try {
          const thumbnailBlob = await captureVideoThumbnail(file);
          if (thumbnailBlob) {
            const thumbForm = new FormData();
            thumbForm.append('file', thumbnailBlob, 'auto-thumbnail.jpg');
            const thumbResp = await axios.post(
              `${API_URL}/api/upload/image`,
              thumbForm,
              { headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
            );
            if (thumbResp.data?.success && thumbResp.data.url) {
              setFormData((prev) => ({ ...prev, video_url: videoUrl, thumbnail_url: thumbResp.data.url }));
              setPreviewThumbnail(thumbResp.data.url);
            }
          }
        } catch (thumbErr) {
          console.warn('Auto-thumbnail failed:', thumbErr);
        }
      }
    } catch (error) {
      console.error('Error uploading video:', error);
      toast.error(i18n.language === 'ar' ? 'خطأ في رفع الفيديو' : 'Error uploading video');
    } finally {
      setUploadingVideo(false);
    }
  };

  const handleThumbnailUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error(i18n.language === 'ar' 
        ? 'صيغة الصورة غير مدعومة. استخدم: JPG, PNG, GIF, WEBP'
        : 'Invalid image type. Use: JPG, PNG, GIF, WEBP'
      );
      return;
    }

    // Validate file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error(i18n.language === 'ar' 
        ? 'حجم الصورة يجب أن يكون أقل من 10MB'
        : 'Image size must be less than 10MB'
      );
      return;
    }

    setUploadingThumbnail(true);
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);

      const response = await axios.post(
        `${API_URL}/api/upload/image`,
        uploadFormData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response.data.success) {
        // Cloudinary returns full URL, use it directly
        const imageUrl = response.data.url;
        setFormData({ ...formData, thumbnail_url: imageUrl });
        setPreviewThumbnail(imageUrl);
        toast.success(i18n.language === 'ar' ? 'تم رفع الصورة المصغرة! ✅' : 'Thumbnail uploaded! ✅');
      }
    } catch (error) {
      console.error('Error uploading thumbnail:', error);
      toast.error(i18n.language === 'ar' ? 'خطأ في رفع الصورة' : 'Error uploading thumbnail');
    } finally {
      setUploadingThumbnail(false);
    }
  };

  const handleRemoveVideo = () => {
    setFormData({ ...formData, video_url: '' });
    setPreviewVideo('');
    if (videoInputRef.current) {
      videoInputRef.current.value = '';
    }
  };

  const handleRemoveThumbnail = () => {
    setFormData({ ...formData, thumbnail_url: '' });
    setPreviewThumbnail('');
    if (thumbnailInputRef.current) {
      thumbnailInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim() || !formData.description.trim() || !formData.video_url.trim()) {
      toast.error(i18n.language === 'ar' ? 'يرجى ملء جميع الحقول المطلوبة' : 'Please fill all required fields');
      return;
    }

    setLoading(true);

    // Build the multi-language object (Swedish only, copied across keys so the
    // frontend can fall back to v.title.sv on every locale). This avoids the
    // slow / occasionally hanging /auto-translate LLM call entirely.
    const sv = formData.title.trim();
    const desc = formData.description.trim();
    const payload = {
      title: { sv, en: sv, ar: sv, tr: sv, fa: sv, so: sv, pl: sv, es: sv, de: sv },
      description: { sv: desc, en: desc, ar: desc, tr: desc, fa: desc, so: desc, pl: desc, es: desc, de: desc },
      video_url: formData.video_url,
      thumbnail_url: formData.thumbnail_url || '',
      duration: parseInt(formData.duration) || 0,
      category: formData.category || 'basics',
      order: parseInt(formData.order) || 0
    };

    try {
      const endpoint = video
        ? `${API}/admin/videos/${video.id}`
        : `${API}/admin/videos`;
      const method = video ? 'put' : 'post';

      const response = await axios[method](endpoint, payload, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 15000
      });

      if (response.data?.success) {
        toast.success(
          video
            ? (i18n.language === 'ar' ? 'تم تحديث الفيديو! 🎉' : 'Video uppdaterad! 🎉')
            : (i18n.language === 'ar' ? 'تمت إضافة الفيديو! 🎉' : 'Video tillagd! 🎉')
        );
        onSuccess();
        onClose();
      }
    } catch (error) {
      console.error('Error saving video:', error);
      const detail = error?.response?.data?.detail || error?.message || 'unknown error';
      toast.error(
        i18n.language === 'ar'
          ? `خطأ في حفظ الفيديو: ${detail}`
          : `Kunde inte spara video: ${detail}`
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Languages className="w-6 h-6 text-emerald-600" />
            {video 
              ? (i18n.language === 'ar' ? 'تعديل الفيديو' : 'Edit Video')
              : (i18n.language === 'ar' ? 'إضافة فيديو جديد' : 'Add New Video')
            }
          </DialogTitle>
          <p className="text-sm text-slate-500 mt-2">
            🇸🇪 {i18n.language === 'ar' 
              ? 'الفيديوهات تُعرض باللغة السويدية فقط لجميع المستخدمين'
              : 'Videor visas endast på svenska för alla användare'
            }
          </p>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6 mt-4">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <Label className="text-sm font-semibold text-amber-900 mb-2 block">
              🇸🇪 {i18n.language === 'ar' ? 'لغة المحتوى' : 'Innehållsspråk'}
            </Label>
            <div className="bg-white border border-amber-300 rounded-md px-3 py-2 text-sm text-slate-800">
              🇸🇪 Svenska
              <span className="ms-2 text-xs text-slate-500">
                ({i18n.language === 'ar' ? 'الفيديوهات بالسويدية فقط' : 'Videor finns endast på svenska'})
              </span>
            </div>
          </div>

          <div>
            <Label className="text-sm font-semibold">
              {i18n.language === 'ar' ? 'العنوان *' : 'Title *'}
            </Label>
            <Input
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder={i18n.language === 'ar' ? 'عنوان الفيديو' : 'Video title'}
              className="mt-2"
              required
            />
          </div>

          <div>
            <Label className="text-sm font-semibold">
              {i18n.language === 'ar' ? 'الوصف *' : 'Description *'}
            </Label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder={i18n.language === 'ar' ? 'وصف الفيديو' : 'Video description'}
              className="mt-2"
              rows={4}
              required
            />
          </div>

          {/* Video Upload Section */}
          <div className="space-y-3 border-2 border-dashed border-emerald-200 rounded-lg p-4 bg-emerald-50/30">
            <Label className="text-sm font-semibold text-emerald-900">
              {i18n.language === 'ar' ? 'الفيديو *' : 'Video *'}
            </Label>
            
            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => videoInputRef.current?.click()}
                disabled={uploadingVideo}
                className="flex items-center gap-2 bg-white"
              >
                {uploadingVideo ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {i18n.language === 'ar' ? 'جاري الرفع...' : 'Uploading...'}
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4" />
                    {i18n.language === 'ar' ? 'رفع فيديو من حاسوبك' : 'Upload from Computer'}
                  </>
                )}
              </Button>
              
              {previewVideo && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleRemoveVideo}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50 bg-white"
                >
                  <X className="w-4 h-4 me-1" />
                  {i18n.language === 'ar' ? 'إزالة' : 'Remove'}
                </Button>
              )}
            </div>

            <input
              ref={videoInputRef}
              type="file"
              accept="video/mp4,video/webm,video/avi,video/quicktime"
              onChange={handleVideoUpload}
              className="hidden"
            />

            {previewVideo && (
              <div className="mt-3 border border-emerald-300 rounded-lg p-3 bg-white">
                <div className="flex items-center gap-2 text-emerald-700 mb-2">
                  <VideoIcon className="w-5 h-5" />
                  <span className="text-sm font-medium">
                    {i18n.language === 'ar' ? 'الفيديو المرفوع:' : 'Uploaded Video:'}
                  </span>
                </div>
                <video 
                  src={previewVideo} 
                  controls 
                  className="w-full max-h-64 rounded-lg"
                />
              </div>
            )}

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-emerald-300"></div>
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="px-2 bg-emerald-50/30 text-slate-600">
                  {i18n.language === 'ar' ? 'أو' : 'OR'}
                </span>
              </div>
            </div>

            <Input
              type="text"
              value={formData.video_url}
              onChange={(e) => {
                setFormData({ ...formData, video_url: e.target.value });
                setPreviewVideo(e.target.value);
              }}
              placeholder={i18n.language === 'ar' ? 'أو الصق رابط الفيديو (YouTube/Vimeo)...' : 'Or paste video URL (YouTube/Vimeo)...'}
              className="bg-white"
              dir="ltr"
            />
          </div>

          {/* Thumbnail Upload Section */}
          <div className="space-y-3">
            <Label className="text-sm font-semibold">
              {i18n.language === 'ar' ? 'الصورة المصغرة (اختياري)' : 'Thumbnail (Optional)'}
            </Label>
            
            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => thumbnailInputRef.current?.click()}
                disabled={uploadingThumbnail}
                className="flex items-center gap-2"
              >
                {uploadingThumbnail ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {i18n.language === 'ar' ? 'جاري الرفع...' : 'Uploading...'}
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4" />
                    {i18n.language === 'ar' ? 'رفع صورة من حاسوبك' : 'Upload from Computer'}
                  </>
                )}
              </Button>
              
              {previewThumbnail && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleRemoveThumbnail}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  <X className="w-4 h-4 me-1" />
                  {i18n.language === 'ar' ? 'إزالة' : 'Remove'}
                </Button>
              )}
            </div>

            <input
              ref={thumbnailInputRef}
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
              onChange={handleThumbnailUpload}
              className="hidden"
            />

            {previewThumbnail && (
              <div className="mt-3 border-2 border-dashed border-slate-200 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-2">
                  <ImageIcon className="w-5 h-5 text-purple-600" />
                  <span className="text-sm text-slate-600">
                    {i18n.language === 'ar' ? 'معاينة الصورة:' : 'Thumbnail Preview:'}
                  </span>
                </div>
                <img 
                  src={previewThumbnail} 
                  alt="Thumbnail" 
                  className="w-full h-auto max-h-48 rounded-lg object-cover"
                />
              </div>
            )}

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-200"></div>
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="px-2 bg-white text-slate-500">
                  {i18n.language === 'ar' ? 'أو' : 'OR'}
                </span>
              </div>
            </div>

            <Input
              type="text"
              value={formData.thumbnail_url}
              onChange={(e) => {
                setFormData({ ...formData, thumbnail_url: e.target.value });
                setPreviewThumbnail(e.target.value);
              }}
              placeholder={i18n.language === 'ar' ? 'أو الصق رابط الصورة المصغرة...' : 'Or paste thumbnail URL...'}
              dir="ltr"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-semibold">
                {i18n.language === 'ar' ? 'المدة (بالثواني)' : 'Duration (seconds)'}
              </Label>
              <Input
                type="number"
                value={formData.duration}
                onChange={(e) => setFormData({ ...formData, duration: parseInt(e.target.value) || 0 })}
                placeholder="600"
                className="mt-2"
                min="0"
              />
            </div>

            <div>
              <Label className="text-sm font-semibold">
                {i18n.language === 'ar' ? 'الترتيب' : 'Order'}
              </Label>
              <Input
                type="number"
                value={formData.order}
                onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                placeholder="1"
                className="mt-2"
                min="0"
              />
            </div>
          </div>

          <div>
            <Label className="text-sm font-semibold">
              {i18n.language === 'ar' ? 'الفئة' : 'Category'}
            </Label>
            <Select value={formData.category} onValueChange={(val) => setFormData({ ...formData, category: val })}>
              <SelectTrigger className="mt-2">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="basics">{i18n.language === 'ar' ? 'الأساسيات' : 'Basics'}</SelectItem>
                <SelectItem value="traffic_signs">{i18n.language === 'ar' ? 'إشارات المرور' : 'Traffic Signs'}</SelectItem>
                <SelectItem value="road_rules">{i18n.language === 'ar' ? 'قواعد الطريق' : 'Road Rules'}</SelectItem>
                <SelectItem value="parking">{i18n.language === 'ar' ? 'الوقوف' : 'Parking'}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
              {i18n.language === 'ar' ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button type="submit" disabled={loading} className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600">
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {i18n.language === 'ar' ? 'جاري الحفظ...' : 'Sparar...'}
                </>
              ) : (
                <>
                  <Languages className="w-4 h-4 mr-2" />
                  {video 
                    ? (i18n.language === 'ar' ? 'تحديث وترجمة' : 'Update & Translate')
                    : (i18n.language === 'ar' ? 'إضافة وترجمة' : 'Add & Translate')
                  }
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default VideoModal;
